import { Fragment, useCallback, useId, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { ClothesImage } from '~/components/collections/ClothesImage';
import { CollectionImage } from '~/components/collections/CollectionImage';
import { CurrencyIcon } from '~/components/collections/CurrencyIcon';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { CloseButton } from '~/components/ui/CloseButton';
import { Divider } from '~/components/ui/Divider';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useInputAction } from '~/rage';
import { CaseItemRarity, Currency, InputAction } from '~/shared';
import {
	setModalBuyVisible,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { formatTime } from '../../utils';
import { CaseItemColors } from '../CaseItem';
import {
	StyledBackNumbers,
	StyledBadgesContainer,
	StyledButtonsGrid,
	StyledContainer,
	StyledDiscountContainer,
	StyledDiscountTimerContainer,
	StyledDonateButtonContent,
	StyledDonateShadow,
	StyledHeadContainer,
	StyledImageVehicleItemContainer,
	StyledItemType,
	StyledModalWrapper,
	StyledMoneyImage,
	StyledNameContainer,
	StyledPriceContainer,
	StyledRarity,
	StyledShadow,
	StyledSvg,
} from './ModalBuy.styled';

const rarityTextMap: Record<CaseItemRarity, string> = {
	[CaseItemRarity.COMMON]: 'ОБЫЧНОЕ',
	[CaseItemRarity.UNCOMMON]: 'НЕОБЫЧНОЕ',
	[CaseItemRarity.RARE]: 'РЕДКОЕ',
	[CaseItemRarity.EPIC]: 'ЭПИЧЕСКОЕ',
	[CaseItemRarity.LEGENDARY]: 'ЛЕГЕНДАРНОЕ',
};

const ModalBuy = () => {
	const dispatch = useLayoutDispatch();
	const id = useId();
	const modalBuyType = useLayoutSelector((state) => state.shop.modalBuyType);
	const modalBuyDonateItem = useLayoutSelector(
		(state) => state.shop.modalBuyDonateItem,
	);
	const modalBuyInventoryItem = useLayoutSelector(
		(state) => state.shop.modalBuyInventoryItem,
	);
	const donateIcon = useWalletIcon(Currency.Donate, true);
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const donateItem = modalBuyType === 'donate' ? modalBuyDonateItem : null;
	const inventoryItem =
		modalBuyType === 'vehicle' || modalBuyType === 'clothes'
			? modalBuyInventoryItem
			: null;
	const amount = donateItem?.amount ?? 0;
	const amountString = useMemo(() => amount.toLocaleString('ru-RU'), [amount]);
	const rarity = inventoryItem?.rarity ?? CaseItemRarity.RARE;
	const itemColor = CaseItemColors[rarity];
	const [discountTime, setDiscountTime] = useState('');

	const handleClose = useCallback(() => {
		dispatch(setModalBuyVisible(false));
	}, [dispatch]);

	const handleCancel = useCallback(() => {
		handleClose();
	}, [handleClose]);

	const handleBuy = useCallback(async () => {
		if (modalBuyType === 'donate') {
			await rage.donate.buyCurrencyShopItem(donateItem?.id ?? '');
			handleClose();
		}

		if (
			(modalBuyType === 'vehicle' || modalBuyType === 'clothes') &&
			inventoryItem
		) {
			await rage.donate.buyDonateShopItem(inventoryItem.donateItemId);
			handleClose();
		}
	}, [modalBuyType, donateItem, inventoryItem, handleClose]);

	useEffectOnce(() => {
		if (!inventoryItem?.discountTimestampEnd) {
			return;
		}

		const update = () => {
			const { time, unit } = formatTime(inventoryItem.discountTimestampEnd!);
			setDiscountTime(unit ? `${time} ${unit}` : time);
		};

		update();
		const interval = setInterval(update, 1000);

		return () => clearInterval(interval);
	});

	useInputAction(InputAction.CommonMenuClose, handleClose);

	return (
		<StyledModalWrapper>
			<StyledContainer>
				<StyledHeadContainer>
					<Text>Информация о товаре</Text>
					<CloseButton
						height={ButtonHeight.Height20}
						color={ButtonColors.Default}
						iconSize={10}
						borderRadius={ButtonBorderRadius.Radius5}
						onClick={handleClose}
					/>
				</StyledHeadContainer>
				<StyledImageVehicleItemContainer>
					{modalBuyType !== 'donate' && (
						<Fragment>
							<StyledShadow style={{ backgroundColor: itemColor }} />
							<StyledSvg
								width="400"
								height="200"
								viewBox="0 0 400 200"
								fill="none"
							>
								<rect
									opacity="0.05"
									width="400"
									height="200"
									rx="5"
									fill={`url(#pattern${id})`}
								/>
								<defs>
									<pattern
										id={`pattern${id}`}
										patternUnits="userSpaceOnUse"
										patternTransform="matrix(125.936 0 0 64.3467 177.019 77.019)"
										preserveAspectRatio="none"
										viewBox="0.00022769 0 251.871 128.693"
										width="1"
										height="1"
									>
										<use
											xlinkHref={`#pattern${id}_inner`}
											transform="translate(0 -128.693)"
										/>
										<use
											xlinkHref={`#pattern${id}_inner`}
											transform="translate(125.936 -64.3467)"
										/>
										<g id={`pattern${id}_inner`}>
											<rect
												x="70.7107"
												width="30"
												height="100"
												transform="rotate(45 70.7107 0)"
												fill={itemColor}
											/>
										</g>
										<use
											xlinkHref={`#pattern${id}_inner`}
											transform="translate(125.936 64.3467)"
										/>
									</pattern>
								</defs>
							</StyledSvg>
							<StyledRarity style={{ backgroundColor: itemColor }} />
						</Fragment>
					)}

					{modalBuyType === 'donate' && (
						<Fragment>
							<StyledDonateShadow />
							<StyledBackNumbers>
								{amountString}
								<br />
								{amountString}&nbsp;{amountString}
								<br />
								{amountString}&nbsp;{amountString}&nbsp;
								{amountString.slice(0, amountString.length - 3)}
							</StyledBackNumbers>
							<StyledMoneyImage />
						</Fragment>
					)}
					{modalBuyType === 'vehicle' && inventoryItem && (
						<CollectionImage
							collection={'vehicles'}
							id={inventoryItem.imageId}
							size={'md'}
							className="collection-image"
						/>
					)}
					{modalBuyType === 'clothes' && inventoryItem && (
						<ClothesImage
							id={inventoryItem.imageId}
							size={160}
							className="collection-image"
						/>
					)}
				</StyledImageVehicleItemContainer>
				<StyledNameContainer>
					{modalBuyType === 'donate' ? (
						<Text style={{ fontSize: 18, color: '#ffffff', opacity: 0.25 }}>
							Вы получите
						</Text>
					) : (
						<Text style={{ fontSize: 18, fontWeight: 500, color: '#ffffff' }}>
							{inventoryItem?.name ?? ''}
						</Text>
					)}
					{modalBuyType === 'donate' && (
						<Button
							color={ButtonColors.Buy}
							height={ButtonHeight.Height30}
							borderRadius={ButtonBorderRadius.Radius5}
							style={{
								pointerEvents: 'none',
								width: 'fit-content',
								padding: '0 10px',
							}}
						>
							<StyledDonateButtonContent>
								<CurrencyIcon icon={rubleIcon} size="sm" />
								<Text>{amountString}</Text>
							</StyledDonateButtonContent>
						</Button>
					)}
					{modalBuyType !== 'donate' && inventoryItem && (
						<StyledItemType style={{ backgroundColor: itemColor }}>
							<Text>{rarityTextMap[rarity]}</Text>
						</StyledItemType>
					)}
				</StyledNameContainer>
				<Divider
					$direction="H"
					style={{ marginTop: 30, marginBottom: 30, opacity: 0.1 }}
				/>
				<StyledPriceContainer>
					<Text>Стоимость товара</Text>
					<Text>
						{modalBuyType === 'donate'
							? donateItem?.price.toLocaleString('ru-RU')
							: inventoryItem?.price.toLocaleString('ru-RU') ?? '0'}
					</Text>
					<CurrencyIcon icon={donateIcon} size="sm" />
				</StyledPriceContainer>
				{inventoryItem && (
					<StyledBadgesContainer style={{ marginTop: 8 }}>
						{discountTime && inventoryItem.discount ? (
							<StyledDiscountTimerContainer>
								<Text>{inventoryItem.discount * 100}%</Text>
								<div className={'divider'} />
								<Text>{discountTime}</Text>
							</StyledDiscountTimerContainer>
						) : inventoryItem.discount ? (
							<StyledDiscountContainer>
								<Text>{inventoryItem.discount * 100}%</Text>
							</StyledDiscountContainer>
						) : null}
					</StyledBadgesContainer>
				)}
				<StyledButtonsGrid>
					<Button
						color={ButtonColors.Default}
						height={ButtonHeight.Height50}
						onClick={handleCancel}
					>
						<Text>Отменить</Text>
					</Button>
					<Button
						color={ButtonColors.Buy}
						height={ButtonHeight.Height50}
						onClick={handleBuy}
					>
						<Text>Оплатить</Text>
					</Button>
				</StyledButtonsGrid>
			</StyledContainer>
		</StyledModalWrapper>
	);
};

export { ModalBuy };
